import {ParamType} from './../../../../../@polygonjs/polygonjs/src/engine/poly/ParamType';
import type {MaterialsNetworkSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/MaterialsNetwork';
import type {GeoObjNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/obj/Geo';
import type {CameraControlsSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/CameraControls';
import type {RootManagerNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/manager/Root';
import type {ExampleData, DataCallbackOptions} from '../../Common';
import {NodeContext} from './../../../../../@polygonjs/polygonjs/src/engine/poly/NodeContext';
import {ReturnedNodeDataDict} from '../../ExampleUtils';
import {ClothSolverSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/ClothSolver';
import {MeshPhysicalBuilderMatNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/mat/MeshPhysicalBuilder';
import {ActorSopNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/sop/Actor';
import {MeshStandardBuilderMatNode} from './../../../../../@polygonjs/polygonjs/src/engine/nodes/mat/MeshStandardBuilder';

export function createData(options: DataCallbackOptions): ExampleData {
	const {scene} = options;
	const root = scene.root();
	const scene_root_nodes: ReturnedNodeDataDict = {};
	function create_geo1(parentNode: RootManagerNode) {
		var geo1 = parentNode.createNode('geo');
		geo1.setName('geo1');
		const geo1_nodes: ReturnedNodeDataDict = {};
		function create_icosahedron1(geo1: GeoObjNode) {
			var icosahedron1 = geo1.createNode('icosahedron');
			icosahedron1.setName('icosahedron1');
			const icosahedron1_nodes: ReturnedNodeDataDict = {};
			icosahedron1.uiData.setPosition(-50, -900);
			icosahedron1.p.detail.set(11);
			icosahedron1.params.postCreateSpareParams();
			icosahedron1.params.runOnSceneLoadHooks();
			return {node: icosahedron1, children: icosahedron1_nodes};
		}
		function create_clothSolver1(geo1: GeoObjNode) {
			var clothSolver1 = geo1.createNode('clothSolver');
			clothSolver1.setName('clothSolver1');
			const clothSolver1_nodes: ReturnedNodeDataDict = {};
			function create_globals1(clothSolver1: ClothSolverSopNode) {
				var globals1 = clothSolver1.createNode('globals');
				globals1.setName('globals1');
				const globals1_nodes: ReturnedNodeDataDict = {};
				globals1.uiData.setPosition(-400, 0);
				globals1.params.postCreateSpareParams();
				globals1.params.runOnSceneLoadHooks();
				return {node: globals1, children: globals1_nodes};
			}
			function create_output1(clothSolver1: ClothSolverSopNode) {
				var output1 = clothSolver1.createNode('output');
				output1.setName('output1');
				const output1_nodes: ReturnedNodeDataDict = {};
				output1.uiData.setPosition(200, 0);
				output1.params.postCreateSpareParams();
				output1.params.runOnSceneLoadHooks();
				return {node: output1, children: output1_nodes};
			}
			clothSolver1_nodes['globals1'] = create_globals1(clothSolver1);
			clothSolver1_nodes['output1'] = create_output1(clothSolver1);
			clothSolver1_nodes['output1'].node.setInput('position', clothSolver1_nodes['globals1'].node, 'position');
			if (clothSolver1.childrenController) {
				clothSolver1.childrenController.selection.set([]);
			}
			clothSolver1.uiData.setComment(
				'This is the solver. You can add more nodes inside it to add specific movements in area of the input geometry. This can be useful for more precise tweaking, like vibrations.'
			);
			clothSolver1.uiData.setPosition(50, 650);
			clothSolver1.params.postCreateSpareParams();
			clothSolver1.params.runOnSceneLoadHooks();
			return {node: clothSolver1, children: clothSolver1_nodes};
		}
		function create_clothPrepare1(geo1: GeoObjNode) {
			var clothPrepare1 = geo1.createNode('clothPrepare');
			clothPrepare1.setName('clothPrepare1');
			const clothPrepare1_nodes: ReturnedNodeDataDict = {};
			clothPrepare1.uiData.setComment('the clothPrepare node adds some attributes, like id, to your geometry.');
			clothPrepare1.uiData.setPosition(50, 150);
			clothPrepare1.params.postCreateSpareParams();
			clothPrepare1.params.runOnSceneLoadHooks();
			return {node: clothPrepare1, children: clothPrepare1_nodes};
		}
		function create_material1(geo1: GeoObjNode) {
			var material1 = geo1.createNode('material');
			material1.setName('material1');
			const material1_nodes: ReturnedNodeDataDict = {};
			material1.uiData.setComment(
				'This assigns the [material](../MAT/meshPhysical_CLOTH), which is necessary to display the result of the simulation.'
			);
			material1.uiData.setPosition(50, 950);
			material1.flags.display.set(true);
			material1.p.material.set('../MAT/meshPhysical_CLOTH');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return {node: material1, children: material1_nodes};
		}
		function create_MAT(geo1: GeoObjNode) {
			var MAT = geo1.createNode('materialsNetwork');
			MAT.setName('MAT');
			const MAT_nodes: ReturnedNodeDataDict = {};
			function create_meshPhysical_CLOTH(MAT: MaterialsNetworkSopNode) {
				var meshPhysical_CLOTH = MAT.createNode('meshPhysicalBuilder');
				meshPhysical_CLOTH.setName('meshPhysical_CLOTH');
				const meshPhysical_CLOTH_nodes: ReturnedNodeDataDict = {};
				function create_globals1(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var globals1 = meshPhysical_CLOTH.createNode('globals');
					globals1.setName('globals1');
					const globals1_nodes: ReturnedNodeDataDict = {};
					globals1.uiData.setPosition(-400, 0);
					globals1.params.postCreateSpareParams();
					globals1.params.runOnSceneLoadHooks();
					return {node: globals1, children: globals1_nodes};
				}
				function create_output1(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var output1 = meshPhysical_CLOTH.createNode('output');
					output1.setName('output1');
					const output1_nodes: ReturnedNodeDataDict = {};
					output1.uiData.setPosition(400, 0);
					output1.params.postCreateSpareParams();
					output1.params.runOnSceneLoadHooks();
					return {node: output1, children: output1_nodes};
				}
				function create_clothSolverPosition1(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var clothSolverPosition1 = meshPhysical_CLOTH.createNode('clothSolverPosition');
					clothSolverPosition1.setName('clothSolverPosition1');
					const clothSolverPosition1_nodes: ReturnedNodeDataDict = {};
					clothSolverPosition1.uiData.setPosition(100, 0);
					clothSolverPosition1.params.postCreateSpareParams();
					clothSolverPosition1.params.runOnSceneLoadHooks();
					return {node: clothSolverPosition1, children: clothSolverPosition1_nodes};
				}
				function create_attribute1(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var attribute1 = meshPhysical_CLOTH.createNode('attribute');
					attribute1.setName('attribute1');
					const attribute1_nodes: ReturnedNodeDataDict = {};
					attribute1.uiData.setPosition(-100, 0);
					attribute1.p.name.set('id');
					attribute1.params.postCreateSpareParams();
					attribute1.params.runOnSceneLoadHooks();
					return {node: attribute1, children: attribute1_nodes};
				}
				function create_attribute2(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var attribute2 = meshPhysical_CLOTH.createNode('attribute');
					attribute2.setName('attribute2');
					const attribute2_nodes: ReturnedNodeDataDict = {};
					attribute2.uiData.setPosition(-900, 550);
					attribute2.p.name.set('restP');
					attribute2.p.type.set(2);
					attribute2.params.postCreateSpareParams();
					attribute2.params.runOnSceneLoadHooks();
					return {node: attribute2, children: attribute2_nodes};
				}
				function create_mix1(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var mix1 = meshPhysical_CLOTH.createNode('mix');
					mix1.setName('mix1');
					const mix1_nodes: ReturnedNodeDataDict = {};
					mix1.uiData.setPosition(250, 300);
					mix1.addParam(ParamType.VECTOR3, 'value0', [0, 0, 0], {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					mix1.addParam(ParamType.VECTOR3, 'value1', [0, 0, 0], {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					mix1.addParam(ParamType.FLOAT, 'blend', 0.5, {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					mix1.params.postCreateSpareParams();
					mix1.params.runOnSceneLoadHooks();
					return {node: mix1, children: mix1_nodes};
				}
				function create_constant1(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var constant1 = meshPhysical_CLOTH.createNode('constant');
					constant1.setName('constant1');
					const constant1_nodes: ReturnedNodeDataDict = {};
					constant1.uiData.setPosition(-100, 300);
					constant1.p.type.set(4);
					constant1.p.color.set([1, 1, 1]);
					constant1.p.asColor.set(true);
					constant1.params.postCreateSpareParams();
					constant1.params.runOnSceneLoadHooks();
					return {node: constant1, children: constant1_nodes};
				}
				function create_constant2(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var constant2 = meshPhysical_CLOTH.createNode('constant');
					constant2.setName('constant2');
					const constant2_nodes: ReturnedNodeDataDict = {};
					constant2.uiData.setPosition(-100, 200);
					constant2.p.type.set(4);
					constant2.p.color.set([0.07036009568874305, 0.5906188409113381, 0.06662593863608139]);
					constant2.p.asColor.set(true);
					constant2.params.postCreateSpareParams();
					constant2.params.runOnSceneLoadHooks();
					return {node: constant2, children: constant2_nodes};
				}
				function create_vec3ToFloat1(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var vec3ToFloat1 = meshPhysical_CLOTH.createNode('vec3ToFloat');
					vec3ToFloat1.setName('vec3ToFloat1');
					const vec3ToFloat1_nodes: ReturnedNodeDataDict = {};
					vec3ToFloat1.uiData.setPosition(-750, 450);
					vec3ToFloat1.params.postCreateSpareParams();
					vec3ToFloat1.params.runOnSceneLoadHooks();
					return {node: vec3ToFloat1, children: vec3ToFloat1_nodes};
				}
				function create_floatToVec2_1(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var floatToVec2_1 = meshPhysical_CLOTH.createNode('floatToVec2');
					floatToVec2_1.setName('floatToVec2_1');
					const floatToVec2_1_nodes: ReturnedNodeDataDict = {};
					floatToVec2_1.uiData.setPosition(-550, 450);
					floatToVec2_1.params.postCreateSpareParams();
					floatToVec2_1.params.runOnSceneLoadHooks();
					return {node: floatToVec2_1, children: floatToVec2_1_nodes};
				}
				function create_vec3ToFloat2(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var vec3ToFloat2 = meshPhysical_CLOTH.createNode('vec3ToFloat');
					vec3ToFloat2.setName('vec3ToFloat2');
					const vec3ToFloat2_nodes: ReturnedNodeDataDict = {};
					vec3ToFloat2.uiData.setPosition(-750, 650);
					vec3ToFloat2.params.postCreateSpareParams();
					vec3ToFloat2.params.runOnSceneLoadHooks();
					return {node: vec3ToFloat2, children: vec3ToFloat2_nodes};
				}
				function create_floatToVec2_2(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var floatToVec2_2 = meshPhysical_CLOTH.createNode('floatToVec2');
					floatToVec2_2.setName('floatToVec2_2');
					const floatToVec2_2_nodes: ReturnedNodeDataDict = {};
					floatToVec2_2.uiData.setPosition(-550, 650);
					floatToVec2_2.params.postCreateSpareParams();
					floatToVec2_2.params.runOnSceneLoadHooks();
					return {node: floatToVec2_2, children: floatToVec2_2_nodes};
				}
				function create_grid1(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var grid1 = meshPhysical_CLOTH.createNode('grid');
					grid1.setName('grid1');
					const grid1_nodes: ReturnedNodeDataDict = {};
					grid1.uiData.setPosition(-250, 500);
					grid1.p.freqMult.set(5.9);
					grid1.params.postCreateSpareParams();
					grid1.params.runOnSceneLoadHooks();
					return {node: grid1, children: grid1_nodes};
				}
				function create_constant3(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var constant3 = meshPhysical_CLOTH.createNode('constant');
					constant3.setName('constant3');
					const constant3_nodes: ReturnedNodeDataDict = {};
					constant3.uiData.setPosition(-550, 800);
					constant3.p.float.set(5);
					constant3.params.postCreateSpareParams();
					constant3.params.runOnSceneLoadHooks();
					return {node: constant3, children: constant3_nodes};
				}
				function create_grid2(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var grid2 = meshPhysical_CLOTH.createNode('grid');
					grid2.setName('grid2');
					const grid2_nodes: ReturnedNodeDataDict = {};
					grid2.uiData.setPosition(-250, 650);
					grid2.p.freqMult.set(5.9);
					grid2.params.postCreateSpareParams();
					grid2.params.runOnSceneLoadHooks();
					return {node: grid2, children: grid2_nodes};
				}
				function create_mult2(meshPhysical_CLOTH: MeshPhysicalBuilderMatNode) {
					var mult2 = meshPhysical_CLOTH.createNode('mult');
					mult2.setName('mult2');
					const mult2_nodes: ReturnedNodeDataDict = {};
					mult2.uiData.setPosition(-100, 550);
					mult2.addParam(ParamType.FLOAT, 'mult0', 1, {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					mult2.addParam(ParamType.FLOAT, 'mult1', 1, {
						spare: true,
						editable: false,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					mult2.addParam(ParamType.FLOAT, 'mult2', 1, {
						spare: true,
						editable: true,
						computeOnDirty: true,
						dependentOnFoundParam: false,
					});
					mult2.params.postCreateSpareParams();
					mult2.params.runOnSceneLoadHooks();
					return {node: mult2, children: mult2_nodes};
				}
				meshPhysical_CLOTH_nodes['globals1'] = create_globals1(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['output1'] = create_output1(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['clothSolverPosition1'] = create_clothSolverPosition1(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['attribute1'] = create_attribute1(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['attribute2'] = create_attribute2(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['mix1'] = create_mix1(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['constant1'] = create_constant1(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['constant2'] = create_constant2(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['vec3ToFloat1'] = create_vec3ToFloat1(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['floatToVec2_1'] = create_floatToVec2_1(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['vec3ToFloat2'] = create_vec3ToFloat2(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['floatToVec2_2'] = create_floatToVec2_2(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['grid1'] = create_grid1(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['constant3'] = create_constant3(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['grid2'] = create_grid2(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['mult2'] = create_mult2(meshPhysical_CLOTH);
				meshPhysical_CLOTH_nodes['output1'].node.setInput(
					'position',
					meshPhysical_CLOTH_nodes['clothSolverPosition1'].node,
					'position'
				);
				meshPhysical_CLOTH_nodes['output1'].node.setInput(
					'color',
					meshPhysical_CLOTH_nodes['mix1'].node,
					'mix'
				);
				meshPhysical_CLOTH_nodes['clothSolverPosition1'].node.setInput(
					'id',
					meshPhysical_CLOTH_nodes['attribute1'].node,
					'val'
				);
				meshPhysical_CLOTH_nodes['mix1'].node.setInput(
					'value0',
					meshPhysical_CLOTH_nodes['constant2'].node,
					'val'
				);
				meshPhysical_CLOTH_nodes['mix1'].node.setInput(
					'value1',
					meshPhysical_CLOTH_nodes['constant1'].node,
					'val'
				);
				meshPhysical_CLOTH_nodes['mix1'].node.setInput(
					'blend',
					meshPhysical_CLOTH_nodes['mult2'].node,
					'product'
				);
				meshPhysical_CLOTH_nodes['vec3ToFloat1'].node.setInput(
					'vec',
					meshPhysical_CLOTH_nodes['attribute2'].node,
					'val'
				);
				meshPhysical_CLOTH_nodes['floatToVec2_1'].node.setInput(
					'x',
					meshPhysical_CLOTH_nodes['vec3ToFloat1'].node,
					'y'
				);
				meshPhysical_CLOTH_nodes['floatToVec2_1'].node.setInput(
					'y',
					meshPhysical_CLOTH_nodes['vec3ToFloat1'].node,
					'z'
				);
				meshPhysical_CLOTH_nodes['vec3ToFloat2'].node.setInput(
					'vec',
					meshPhysical_CLOTH_nodes['attribute2'].node,
					'val'
				);
				meshPhysical_CLOTH_nodes['floatToVec2_2'].node.setInput(
					'x',
					meshPhysical_CLOTH_nodes['vec3ToFloat2'].node,
					'x'
				);
				meshPhysical_CLOTH_nodes['floatToVec2_2'].node.setInput(
					'y',
					meshPhysical_CLOTH_nodes['vec3ToFloat2'].node,
					'y'
				);
				meshPhysical_CLOTH_nodes['grid1'].node.setInput(
					'uv',
					meshPhysical_CLOTH_nodes['floatToVec2_1'].node,
					'vec2'
				);
				meshPhysical_CLOTH_nodes['grid1'].node.setInput(
					'freqMult',
					meshPhysical_CLOTH_nodes['constant3'].node,
					'val'
				);
				meshPhysical_CLOTH_nodes['grid2'].node.setInput(
					'uv',
					meshPhysical_CLOTH_nodes['floatToVec2_2'].node,
					'vec2'
				);
				meshPhysical_CLOTH_nodes['grid2'].node.setInput(
					'freqMult',
					meshPhysical_CLOTH_nodes['constant3'].node,
					'val'
				);
				meshPhysical_CLOTH_nodes['mult2'].node.setInput(
					'mult0',
					meshPhysical_CLOTH_nodes['grid1'].node,
					'grid'
				);
				meshPhysical_CLOTH_nodes['mult2'].node.setInput(
					'mult1',
					meshPhysical_CLOTH_nodes['grid2'].node,
					'grid'
				);
				if (meshPhysical_CLOTH.childrenController) {
					meshPhysical_CLOTH.childrenController.selection.set([]);
				}
				meshPhysical_CLOTH.uiData.setPosition(0, 0);
				meshPhysical_CLOTH.addParam(ParamType.VECTOR2, 'tSize', [1, 1], {
					spare: true,
					computeOnDirty: true,
					cook: false,
					dependentOnFoundNode: true,
				});
				meshPhysical_CLOTH.addParam(ParamType.NODE_PATH, 'tPosition0', '', {
					spare: true,
					computeOnDirty: true,
					cook: false,
					dependentOnFoundNode: true,
					nodeSelection: {context: NodeContext.COP},
				});
				meshPhysical_CLOTH.addParam(ParamType.NODE_PATH, 'tPosition1', '', {
					spare: true,
					computeOnDirty: true,
					cook: false,
					dependentOnFoundNode: true,
					nodeSelection: {context: NodeContext.COP},
				});
				meshPhysical_CLOTH.addParam(ParamType.NODE_PATH, 'tNormal', '', {
					spare: true,
					computeOnDirty: true,
					cook: false,
					dependentOnFoundNode: true,
					nodeSelection: {context: NodeContext.COP},
				});
				meshPhysical_CLOTH.params.postCreateSpareParams();
				meshPhysical_CLOTH.params.runOnSceneLoadHooks();
				return {node: meshPhysical_CLOTH, children: meshPhysical_CLOTH_nodes};
			}
			MAT_nodes['meshPhysical_CLOTH'] = create_meshPhysical_CLOTH(MAT);
			if (MAT.childrenController) {
				MAT.childrenController.selection.set([]);
			}
			MAT.uiData.setPosition(-150, 650);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return {node: MAT, children: MAT_nodes};
		}
		function create_actor_clothSolver1(geo1: GeoObjNode) {
			var actor_clothSolver1 = geo1.createNode('actor');
			actor_clothSolver1.setName('actor_clothSolver1');
			const actor_clothSolver1_nodes: ReturnedNodeDataDict = {};
			function create_onScenePause1(actor_clothSolver1: ActorSopNode) {
				var onScenePause1 = actor_clothSolver1.createNode('onScenePause');
				onScenePause1.setName('onScenePause1');
				const onScenePause1_nodes: ReturnedNodeDataDict = {};
				onScenePause1.uiData.setPosition(-100, -300);
				onScenePause1.params.postCreateSpareParams();
				onScenePause1.params.runOnSceneLoadHooks();
				return {node: onScenePause1, children: onScenePause1_nodes};
			}
			function create_onTick1(actor_clothSolver1: ActorSopNode) {
				var onTick1 = actor_clothSolver1.createNode('onTick');
				onTick1.setName('onTick1');
				const onTick1_nodes: ReturnedNodeDataDict = {};
				onTick1.uiData.setPosition(-100, 0);
				onTick1.params.postCreateSpareParams();
				onTick1.params.runOnSceneLoadHooks();
				return {node: onTick1, children: onTick1_nodes};
			}
			function create_clothSolverReset1(actor_clothSolver1: ActorSopNode) {
				var clothSolverReset1 = actor_clothSolver1.createNode('clothSolverReset');
				clothSolverReset1.setName('clothSolverReset1');
				const clothSolverReset1_nodes: ReturnedNodeDataDict = {};
				clothSolverReset1.uiData.setComment(
					'when the scene pauses, the cloth solver will reset. Unplug the onScenePause node if you do not want this behavior.'
				);
				clothSolverReset1.uiData.setPosition(100, -300);
				clothSolverReset1.params.postCreateSpareParams();
				clothSolverReset1.params.runOnSceneLoadHooks();
				return {node: clothSolverReset1, children: clothSolverReset1_nodes};
			}
			function create_clothSolverStepSimulation1(actor_clothSolver1: ActorSopNode) {
				var clothSolverStepSimulation1 = actor_clothSolver1.createNode('clothSolverStepSimulation');
				clothSolverStepSimulation1.setName('clothSolverStepSimulation1');
				const clothSolverStepSimulation1_nodes: ReturnedNodeDataDict = {};
				clothSolverStepSimulation1.uiData.setComment(
					'this needs to be triggered every frame, using the onTick node, in order to simulate the cloth'
				);
				clothSolverStepSimulation1.uiData.setPosition(100, 0);
				clothSolverStepSimulation1.params.postCreateSpareParams();
				clothSolverStepSimulation1.params.runOnSceneLoadHooks();
				return {node: clothSolverStepSimulation1, children: clothSolverStepSimulation1_nodes};
			}
			function create_clothSolverUpdateMaterial1(actor_clothSolver1: ActorSopNode) {
				var clothSolverUpdateMaterial1 = actor_clothSolver1.createNode('clothSolverUpdateMaterial');
				clothSolverUpdateMaterial1.setName('clothSolverUpdateMaterial1');
				const clothSolverUpdateMaterial1_nodes: ReturnedNodeDataDict = {};
				clothSolverUpdateMaterial1.uiData.setComment(
					'The result of the simulation is stored in textures. This node makes sure that the material is given those textures, so that we can display the result of the sim.'
				);
				clothSolverUpdateMaterial1.uiData.setPosition(450, 0);
				clothSolverUpdateMaterial1.params.postCreateSpareParams();
				clothSolverUpdateMaterial1.params.runOnSceneLoadHooks();
				return {node: clothSolverUpdateMaterial1, children: clothSolverUpdateMaterial1_nodes};
			}
			function create_onObjectPointerdown1(actor_clothSolver1: ActorSopNode) {
				var onObjectPointerdown1 = actor_clothSolver1.createNode('onObjectPointerdown');
				onObjectPointerdown1.setName('onObjectPointerdown1');
				const onObjectPointerdown1_nodes: ReturnedNodeDataDict = {};
				onObjectPointerdown1.uiData.setComment(
					'when we click on the object, we create a constraint used by the cloth solver.\n\nThe constraint is created using the `id` attribute found under the cursor.'
				);
				onObjectPointerdown1.uiData.setPosition(-600, 650);
				onObjectPointerdown1.p.traverseChildren.set(false);
				onObjectPointerdown1.params.postCreateSpareParams();
				onObjectPointerdown1.params.runOnSceneLoadHooks();
				return {node: onObjectPointerdown1, children: onObjectPointerdown1_nodes};
			}
			function create_GetIntersectionAttribute1(actor_clothSolver1: ActorSopNode) {
				var GetIntersectionAttribute1 = actor_clothSolver1.createNode('getIntersectionAttribute');
				GetIntersectionAttribute1.setName('GetIntersectionAttribute1');
				const GetIntersectionAttribute1_nodes: ReturnedNodeDataDict = {};
				GetIntersectionAttribute1.uiData.setPosition(-300, 800);
				GetIntersectionAttribute1.p.type.set(3);
				GetIntersectionAttribute1.p.interpolated.set(false);
				GetIntersectionAttribute1.addParam(ParamType.STRING, 'attribName', '', {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				GetIntersectionAttribute1.params.get('attribName')!.set('id');
				GetIntersectionAttribute1.addParam(ParamType.INTEGER, 'notFoundValue', -1, {
					spare: true,
					editable: true,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				GetIntersectionAttribute1.params.postCreateSpareParams();
				GetIntersectionAttribute1.params.runOnSceneLoadHooks();
				return {node: GetIntersectionAttribute1, children: GetIntersectionAttribute1_nodes};
			}
			function create_clothSolverSetSelectedVertexPosition1(actor_clothSolver1: ActorSopNode) {
				var clothSolverSetSelectedVertexPosition1 = actor_clothSolver1.createNode('setClothConstraintPosition');
				clothSolverSetSelectedVertexPosition1.setName('clothSolverSetSelectedVertexPosition1');
				const clothSolverSetSelectedVertexPosition1_nodes: ReturnedNodeDataDict = {};
				clothSolverSetSelectedVertexPosition1.uiData.setComment(
					'Here we set the position of the constraint created by the nodes on the left.\nThe position is set by sending a ray from the camera to the cursor, and seeing where it intersects on a plane.'
				);
				clothSolverSetSelectedVertexPosition1.uiData.setPosition(1400, 400);
				clothSolverSetSelectedVertexPosition1.params.postCreateSpareParams();
				clothSolverSetSelectedVertexPosition1.params.runOnSceneLoadHooks();
				return {
					node: clothSolverSetSelectedVertexPosition1,
					children: clothSolverSetSelectedVertexPosition1_nodes,
				};
			}
			function create_constant2(actor_clothSolver1: ActorSopNode) {
				var constant2 = actor_clothSolver1.createNode('constant');
				constant2.setName('constant2');
				const constant2_nodes: ReturnedNodeDataDict = {};
				constant2.uiData.setPosition(550, 900);
				constant2.p.float.set(-0.68);
				constant2.params.postCreateSpareParams();
				constant2.params.runOnSceneLoadHooks();
				return {node: constant2, children: constant2_nodes};
			}
			function create_null1(actor_clothSolver1: ActorSopNode) {
				var null1 = actor_clothSolver1.createNode('null');
				null1.setName('null1');
				const null1_nodes: ReturnedNodeDataDict = {};
				null1.uiData.setPosition(1150, 800);
				null1.addParam(ParamType.VECTOR3, 'in', [0, 0, 0], {
					spare: true,
					editable: false,
					computeOnDirty: true,
					dependentOnFoundParam: false,
				});
				null1.params.postCreateSpareParams();
				null1.params.runOnSceneLoadHooks();
				return {node: null1, children: null1_nodes};
			}
			function create_plane1(actor_clothSolver1: ActorSopNode) {
				var plane1 = actor_clothSolver1.createNode('plane');
				plane1.setName('plane1');
				const plane1_nodes: ReturnedNodeDataDict = {};
				plane1.uiData.setPosition(750, 900);
				plane1.p.normal.set([0, 0, 1]);
				plane1.params.postCreateSpareParams();
				plane1.params.runOnSceneLoadHooks();
				return {node: plane1, children: plane1_nodes};
			}
			function create_rayFromCursor1(actor_clothSolver1: ActorSopNode) {
				var rayFromCursor1 = actor_clothSolver1.createNode('rayFromCursor');
				rayFromCursor1.setName('rayFromCursor1');
				const rayFromCursor1_nodes: ReturnedNodeDataDict = {};
				rayFromCursor1.uiData.setPosition(750, 750);
				rayFromCursor1.p.element.set(1);
				rayFromCursor1.params.postCreateSpareParams();
				rayFromCursor1.params.runOnSceneLoadHooks();
				return {node: rayFromCursor1, children: rayFromCursor1_nodes};
			}
			function create_rayIntersectPlane1(actor_clothSolver1: ActorSopNode) {
				var rayIntersectPlane1 = actor_clothSolver1.createNode('rayIntersectPlane');
				rayIntersectPlane1.setName('rayIntersectPlane1');
				const rayIntersectPlane1_nodes: ReturnedNodeDataDict = {};
				rayIntersectPlane1.uiData.setPosition(900, 800);
				rayIntersectPlane1.params.postCreateSpareParams();
				rayIntersectPlane1.params.runOnSceneLoadHooks();
				return {node: rayIntersectPlane1, children: rayIntersectPlane1_nodes};
			}
			function create_anyTrigger1(actor_clothSolver1: ActorSopNode) {
				var anyTrigger1 = actor_clothSolver1.createNode('anyTrigger');
				anyTrigger1.setName('anyTrigger1');
				const anyTrigger1_nodes: ReturnedNodeDataDict = {};
				anyTrigger1.uiData.setPosition(100, 400);
				anyTrigger1.params.postCreateSpareParams();
				anyTrigger1.params.runOnSceneLoadHooks();
				return {node: anyTrigger1, children: anyTrigger1_nodes};
			}
			function create_onPointerup1(actor_clothSolver1: ActorSopNode) {
				var onPointerup1 = actor_clothSolver1.createNode('onPointerup');
				onPointerup1.setName('onPointerup1');
				const onPointerup1_nodes: ReturnedNodeDataDict = {};
				onPointerup1.uiData.setComment('when the user releases the cursor, we delete the contraint');
				onPointerup1.uiData.setPosition(-600, 1050);
				onPointerup1.p.element.set(1);
				onPointerup1.params.postCreateSpareParams();
				onPointerup1.params.runOnSceneLoadHooks();
				return {node: onPointerup1, children: onPointerup1_nodes};
			}
			function create_createClothConstraint1(actor_clothSolver1: ActorSopNode) {
				var createClothConstraint1 = actor_clothSolver1.createNode('createClothConstraint');
				createClothConstraint1.setName('createClothConstraint1');
				const createClothConstraint1_nodes: ReturnedNodeDataDict = {};
				createClothConstraint1.uiData.setPosition(-100, 650);
				createClothConstraint1.params.postCreateSpareParams();
				createClothConstraint1.params.runOnSceneLoadHooks();
				return {node: createClothConstraint1, children: createClothConstraint1_nodes};
			}
			function create_deleteClothConstraint1(actor_clothSolver1: ActorSopNode) {
				var deleteClothConstraint1 = actor_clothSolver1.createNode('deleteClothConstraint');
				deleteClothConstraint1.setName('deleteClothConstraint1');
				const deleteClothConstraint1_nodes: ReturnedNodeDataDict = {};
				deleteClothConstraint1.uiData.setPosition(-50, 1050);
				deleteClothConstraint1.params.postCreateSpareParams();
				deleteClothConstraint1.params.runOnSceneLoadHooks();
				return {node: deleteClothConstraint1, children: deleteClothConstraint1_nodes};
			}
			actor_clothSolver1_nodes['onScenePause1'] = create_onScenePause1(actor_clothSolver1);
			actor_clothSolver1_nodes['onTick1'] = create_onTick1(actor_clothSolver1);
			actor_clothSolver1_nodes['clothSolverReset1'] = create_clothSolverReset1(actor_clothSolver1);
			actor_clothSolver1_nodes['clothSolverStepSimulation1'] =
				create_clothSolverStepSimulation1(actor_clothSolver1);
			actor_clothSolver1_nodes['clothSolverUpdateMaterial1'] =
				create_clothSolverUpdateMaterial1(actor_clothSolver1);
			actor_clothSolver1_nodes['onObjectPointerdown1'] = create_onObjectPointerdown1(actor_clothSolver1);
			actor_clothSolver1_nodes['GetIntersectionAttribute1'] =
				create_GetIntersectionAttribute1(actor_clothSolver1);
			actor_clothSolver1_nodes['clothSolverSetSelectedVertexPosition1'] =
				create_clothSolverSetSelectedVertexPosition1(actor_clothSolver1);
			actor_clothSolver1_nodes['constant2'] = create_constant2(actor_clothSolver1);
			actor_clothSolver1_nodes['null1'] = create_null1(actor_clothSolver1);
			actor_clothSolver1_nodes['plane1'] = create_plane1(actor_clothSolver1);
			actor_clothSolver1_nodes['rayFromCursor1'] = create_rayFromCursor1(actor_clothSolver1);
			actor_clothSolver1_nodes['rayIntersectPlane1'] = create_rayIntersectPlane1(actor_clothSolver1);
			actor_clothSolver1_nodes['anyTrigger1'] = create_anyTrigger1(actor_clothSolver1);
			actor_clothSolver1_nodes['onPointerup1'] = create_onPointerup1(actor_clothSolver1);
			actor_clothSolver1_nodes['createClothConstraint1'] = create_createClothConstraint1(actor_clothSolver1);
			actor_clothSolver1_nodes['deleteClothConstraint1'] = create_deleteClothConstraint1(actor_clothSolver1);
			actor_clothSolver1_nodes['clothSolverReset1'].node.setInput(
				'trigger',
				actor_clothSolver1_nodes['onScenePause1'].node,
				'trigger'
			);
			actor_clothSolver1_nodes['clothSolverStepSimulation1'].node.setInput(
				'trigger',
				actor_clothSolver1_nodes['onTick1'].node,
				'trigger'
			);
			actor_clothSolver1_nodes['clothSolverUpdateMaterial1'].node.setInput(
				'trigger',
				actor_clothSolver1_nodes['clothSolverStepSimulation1'].node,
				'trigger'
			);
			actor_clothSolver1_nodes['clothSolverUpdateMaterial1'].node.setInput(
				'tSize',
				actor_clothSolver1_nodes['clothSolverStepSimulation1'].node,
				'tSize'
			);
			actor_clothSolver1_nodes['clothSolverUpdateMaterial1'].node.setInput(
				'tPosition0',
				actor_clothSolver1_nodes['clothSolverStepSimulation1'].node,
				'tPosition0'
			);
			actor_clothSolver1_nodes['clothSolverUpdateMaterial1'].node.setInput(
				'tPosition1',
				actor_clothSolver1_nodes['clothSolverStepSimulation1'].node,
				'tPosition1'
			);
			actor_clothSolver1_nodes['clothSolverUpdateMaterial1'].node.setInput(
				'tNormal',
				actor_clothSolver1_nodes['clothSolverStepSimulation1'].node,
				'tNormal'
			);
			actor_clothSolver1_nodes['GetIntersectionAttribute1'].node.setInput(
				'Intersection',
				actor_clothSolver1_nodes['onObjectPointerdown1'].node,
				'Intersection'
			);
			actor_clothSolver1_nodes['clothSolverSetSelectedVertexPosition1'].node.setInput(
				'trigger',
				actor_clothSolver1_nodes['anyTrigger1'].node,
				'trigger'
			);
			actor_clothSolver1_nodes['clothSolverSetSelectedVertexPosition1'].node.setInput(
				'position',
				actor_clothSolver1_nodes['null1'].node,
				'out'
			);
			actor_clothSolver1_nodes['null1'].node.setInput(
				'in',
				actor_clothSolver1_nodes['rayIntersectPlane1'].node,
				'position'
			);
			actor_clothSolver1_nodes['plane1'].node.setInput(
				'constant',
				actor_clothSolver1_nodes['constant2'].node,
				'val'
			);
			actor_clothSolver1_nodes['rayIntersectPlane1'].node.setInput(
				'Ray',
				actor_clothSolver1_nodes['rayFromCursor1'].node,
				'Ray'
			);
			actor_clothSolver1_nodes['rayIntersectPlane1'].node.setInput(
				'Plane',
				actor_clothSolver1_nodes['plane1'].node,
				'Plane'
			);
			actor_clothSolver1_nodes['anyTrigger1'].node.setInput(
				'trigger0',
				actor_clothSolver1_nodes['onTick1'].node,
				'trigger'
			);
			actor_clothSolver1_nodes['anyTrigger1'].node.setInput(
				'trigger1',
				actor_clothSolver1_nodes['createClothConstraint1'].node,
				'trigger'
			);
			actor_clothSolver1_nodes['createClothConstraint1'].node.setInput(
				'trigger',
				actor_clothSolver1_nodes['onObjectPointerdown1'].node,
				'trigger'
			);
			actor_clothSolver1_nodes['createClothConstraint1'].node.setInput(
				'index',
				actor_clothSolver1_nodes['GetIntersectionAttribute1'].node,
				'val'
			);
			actor_clothSolver1_nodes['deleteClothConstraint1'].node.setInput(
				'trigger',
				actor_clothSolver1_nodes['onPointerup1'].node,
				'trigger'
			);
			if (actor_clothSolver1.childrenController) {
				actor_clothSolver1.childrenController.selection.set([
					actor_clothSolver1_nodes['deleteClothConstraint1'].node,
				]);
			}
			actor_clothSolver1.uiData.setComment(
				'most of the logic of how the cloth solver will behave is inside [this node](.)'
			);
			actor_clothSolver1.uiData.setPosition(50, 400);
			actor_clothSolver1.params.postCreateSpareParams();
			actor_clothSolver1.params.runOnSceneLoadHooks();
			return {node: actor_clothSolver1, children: actor_clothSolver1_nodes};
		}
		function create_restAttributes1(geo1: GeoObjNode) {
			var restAttributes1 = geo1.createNode('restAttributes');
			restAttributes1.setName('restAttributes1');
			const restAttributes1_nodes: ReturnedNodeDataDict = {};
			restAttributes1.uiData.setComment(
				'the rest attribute is only used in the [material](../MAT/meshPhysical_CLOTH), and is not necessary for the simulation itself.'
			);
			restAttributes1.uiData.setPosition(50, -50);
			restAttributes1.params.postCreateSpareParams();
			restAttributes1.params.runOnSceneLoadHooks();
			return {node: restAttributes1, children: restAttributes1_nodes};
		}
		function create_fileOBJ1(geo1: GeoObjNode) {
			var fileOBJ1 = geo1.createNode('fileOBJ');
			fileOBJ1.setName('fileOBJ1');
			const fileOBJ1_nodes: ReturnedNodeDataDict = {};
			fileOBJ1.uiData.setComment(
				'the geometry imported in this node has been created with Houdini. You can use any modeling tool in Houdini to create your character, as long as you make sure to use a remesh node at the end, to have evenly spread out triangles.\n\nYou can look at [the houdini scene used for this character](https://github.com/polygonjs/polygonjs-assets/tree/master/houdini/scenes)'
			);
			fileOBJ1.uiData.setPosition(250, -1150);
			fileOBJ1.p.url.set(
				'https://raw.githubusercontent.com/polygonjs/polygonjs-assets/master/models/cloth/cloth_character_01.obj'
			);
			fileOBJ1.params.postCreateSpareParams();
			fileOBJ1.params.runOnSceneLoadHooks();
			return {node: fileOBJ1, children: fileOBJ1_nodes};
		}
		function create_hierarchy1(geo1: GeoObjNode) {
			var hierarchy1 = geo1.createNode('hierarchy');
			hierarchy1.setName('hierarchy1');
			const hierarchy1_nodes: ReturnedNodeDataDict = {};
			hierarchy1.uiData.setPosition(250, -800);
			hierarchy1.p.mode.set(1);
			hierarchy1.params.postCreateSpareParams();
			hierarchy1.params.runOnSceneLoadHooks();
			return {node: hierarchy1, children: hierarchy1_nodes};
		}
		function create_transform1(geo1: GeoObjNode) {
			var transform1 = geo1.createNode('transform');
			transform1.setName('transform1');
			const transform1_nodes: ReturnedNodeDataDict = {};
			transform1.uiData.setPosition(250, -700);
			transform1.p.scale.set(2);
			transform1.params.postCreateSpareParams();
			transform1.params.runOnSceneLoadHooks();
			return {node: transform1, children: transform1_nodes};
		}
		function create_objectProperties1(geo1: GeoObjNode) {
			var objectProperties1 = geo1.createNode('objectProperties');
			objectProperties1.setName('objectProperties1');
			const objectProperties1_nodes: ReturnedNodeDataDict = {};
			objectProperties1.uiData.setPosition(250, -600);
			objectProperties1.p.tcastShadow.set(true);
			objectProperties1.p.castShadow.set(true);
			objectProperties1.params.postCreateSpareParams();
			objectProperties1.params.runOnSceneLoadHooks();
			return {node: objectProperties1, children: objectProperties1_nodes};
		}
		function create_switch_CHARACTER(geo1: GeoObjNode) {
			var switch_CHARACTER = geo1.createNode('switch');
			switch_CHARACTER.setName('switch_CHARACTER');
			const switch_CHARACTER_nodes: ReturnedNodeDataDict = {};
			switch_CHARACTER.uiData.setComment(
				'The switch allows to test different inputs for the cloth solver.\n- left input is a simple icosahedron (which is like a sphere, but with the triangles laid out evenly, which is necessary for the cloth solver)\n- right input is an imported geometry, created with Houdini'
			);
			switch_CHARACTER.uiData.setPosition(50, -450);
			switch_CHARACTER.params.postCreateSpareParams();
			switch_CHARACTER.params.runOnSceneLoadHooks();
			return {node: switch_CHARACTER, children: switch_CHARACTER_nodes};
		}
		geo1_nodes['icosahedron1'] = create_icosahedron1(geo1);
		geo1_nodes['clothSolver1'] = create_clothSolver1(geo1);
		geo1_nodes['clothPrepare1'] = create_clothPrepare1(geo1);
		geo1_nodes['material1'] = create_material1(geo1);
		geo1_nodes['MAT'] = create_MAT(geo1);
		geo1_nodes['actor_clothSolver1'] = create_actor_clothSolver1(geo1);
		geo1_nodes['restAttributes1'] = create_restAttributes1(geo1);
		geo1_nodes['fileOBJ1'] = create_fileOBJ1(geo1);
		geo1_nodes['hierarchy1'] = create_hierarchy1(geo1);
		geo1_nodes['transform1'] = create_transform1(geo1);
		geo1_nodes['objectProperties1'] = create_objectProperties1(geo1);
		geo1_nodes['switch_CHARACTER'] = create_switch_CHARACTER(geo1);
		geo1_nodes['clothSolver1'].node.setInput(0, geo1_nodes['actor_clothSolver1'].node);
		geo1_nodes['clothPrepare1'].node.setInput(0, geo1_nodes['restAttributes1'].node);
		geo1_nodes['material1'].node.setInput(0, geo1_nodes['clothSolver1'].node);
		geo1_nodes['actor_clothSolver1'].node.setInput(0, geo1_nodes['clothPrepare1'].node);
		geo1_nodes['restAttributes1'].node.setInput(0, geo1_nodes['switch_CHARACTER'].node);
		geo1_nodes['hierarchy1'].node.setInput(0, geo1_nodes['fileOBJ1'].node);
		geo1_nodes['transform1'].node.setInput(0, geo1_nodes['hierarchy1'].node);
		geo1_nodes['objectProperties1'].node.setInput(0, geo1_nodes['transform1'].node);
		geo1_nodes['switch_CHARACTER'].node.setInput(0, geo1_nodes['icosahedron1'].node);
		geo1_nodes['switch_CHARACTER'].node.setInput(1, geo1_nodes['objectProperties1'].node);
		if (geo1.childrenController) {
			geo1.childrenController.selection.set([]);
		}
		geo1.uiData.setPosition(-50, -350);
		geo1.flags.display.set(true);
		geo1.params.postCreateSpareParams();
		geo1.params.runOnSceneLoadHooks();
		return {node: geo1, children: geo1_nodes};
	}
	function create_ground(parentNode: RootManagerNode) {
		var ground = parentNode.createNode('geo');
		ground.setName('ground');
		const ground_nodes: ReturnedNodeDataDict = {};
		function create_material1(ground: GeoObjNode) {
			var material1 = ground.createNode('material');
			material1.setName('material1');
			const material1_nodes: ReturnedNodeDataDict = {};
			material1.uiData.setPosition(0, 300);
			material1.flags.display.set(true);
			material1.p.material.set('../MAT/meshStandardBuilder1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return {node: material1, children: material1_nodes};
		}
		function create_MAT(ground: GeoObjNode) {
			var MAT = ground.createNode('materialsNetwork');
			MAT.setName('MAT');
			const MAT_nodes: ReturnedNodeDataDict = {};
			function create_meshStandardBuilder1(MAT: MaterialsNetworkSopNode) {
				var meshStandardBuilder1 = MAT.createNode('meshStandardBuilder');
				meshStandardBuilder1.setName('meshStandardBuilder1');
				const meshStandardBuilder1_nodes: ReturnedNodeDataDict = {};
				function create_globals1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var globals1 = meshStandardBuilder1.createNode('globals');
					globals1.setName('globals1');
					const globals1_nodes: ReturnedNodeDataDict = {};
					globals1.uiData.setPosition(-450, 0);
					globals1.params.postCreateSpareParams();
					globals1.params.runOnSceneLoadHooks();
					return {node: globals1, children: globals1_nodes};
				}
				function create_output1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var output1 = meshStandardBuilder1.createNode('output');
					output1.setName('output1');
					const output1_nodes: ReturnedNodeDataDict = {};
					output1.uiData.setPosition(400, 0);
					output1.params.postCreateSpareParams();
					output1.params.runOnSceneLoadHooks();
					return {node: output1, children: output1_nodes};
				}
				function create_vec3ToFloat1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var vec3ToFloat1 = meshStandardBuilder1.createNode('vec3ToFloat');
					vec3ToFloat1.setName('vec3ToFloat1');
					const vec3ToFloat1_nodes: ReturnedNodeDataDict = {};
					vec3ToFloat1.uiData.setPosition(-200, 200);
					vec3ToFloat1.params.postCreateSpareParams();
					vec3ToFloat1.params.runOnSceneLoadHooks();
					return {node: vec3ToFloat1, children: vec3ToFloat1_nodes};
				}
				function create_floatToVec2_1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var floatToVec2_1 = meshStandardBuilder1.createNode('floatToVec2');
					floatToVec2_1.setName('floatToVec2_1');
					const floatToVec2_1_nodes: ReturnedNodeDataDict = {};
					floatToVec2_1.uiData.setPosition(-100, 200);
					floatToVec2_1.params.postCreateSpareParams();
					floatToVec2_1.params.runOnSceneLoadHooks();
					return {node: floatToVec2_1, children: floatToVec2_1_nodes};
				}
				function create_checkers1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var checkers1 = meshStandardBuilder1.createNode('checkers');
					checkers1.setName('checkers1');
					const checkers1_nodes: ReturnedNodeDataDict = {};
					checkers1.uiData.setPosition(50, 200);
					checkers1.params.postCreateSpareParams();
					checkers1.params.runOnSceneLoadHooks();
					return {node: checkers1, children: checkers1_nodes};
				}
				function create_mix1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var mix1 = meshStandardBuilder1.createNode('mix');
					mix1.setName('mix1');
					const mix1_nodes: ReturnedNodeDataDict = {};
					mix1.uiData.setPosition(250, 0);
					mix1.addParam(ParamType.VECTOR3, 'value0', [0, 0, 0], {spare: true, editable: false});
					mix1.addParam(ParamType.VECTOR3, 'value1', [0, 0, 0], {spare: true, editable: false});
					mix1.addParam(ParamType.FLOAT, 'blend', 0.5, {spare: true, editable: false});
					mix1.params.postCreateSpareParams();
					mix1.params.runOnSceneLoadHooks();
					return {node: mix1, children: mix1_nodes};
				}
				function create_constant1(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var constant1 = meshStandardBuilder1.createNode('constant');
					constant1.setName('constant1');
					const constant1_nodes: ReturnedNodeDataDict = {};
					constant1.uiData.setPosition(50, -150);
					constant1.p.type.set(4);
					constant1.p.color.set([0.24313725490196078, 0.5098039215686274, 0.8549019607843137]);
					constant1.p.asColor.set(true);
					constant1.params.postCreateSpareParams();
					constant1.params.runOnSceneLoadHooks();
					return {node: constant1, children: constant1_nodes};
				}
				function create_constant2(meshStandardBuilder1: MeshStandardBuilderMatNode) {
					var constant2 = meshStandardBuilder1.createNode('constant');
					constant2.setName('constant2');
					const constant2_nodes: ReturnedNodeDataDict = {};
					constant2.uiData.setPosition(0, 0);
					constant2.p.type.set(4);
					constant2.p.color.set([0.047058823529411764, 0.10196078431372549, 0.17647058823529413]);
					constant2.p.asColor.set(true);
					constant2.params.postCreateSpareParams();
					constant2.params.runOnSceneLoadHooks();
					return {node: constant2, children: constant2_nodes};
				}
				meshStandardBuilder1_nodes['globals1'] = create_globals1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['output1'] = create_output1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['vec3ToFloat1'] = create_vec3ToFloat1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['floatToVec2_1'] = create_floatToVec2_1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['checkers1'] = create_checkers1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['mix1'] = create_mix1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['constant1'] = create_constant1(meshStandardBuilder1);
				meshStandardBuilder1_nodes['constant2'] = create_constant2(meshStandardBuilder1);
				meshStandardBuilder1_nodes['output1'].node.setInput(
					'color',
					meshStandardBuilder1_nodes['mix1'].node,
					'mix'
				);
				meshStandardBuilder1_nodes['vec3ToFloat1'].node.setInput(
					'vec',
					meshStandardBuilder1_nodes['globals1'].node,
					'position'
				);
				meshStandardBuilder1_nodes['floatToVec2_1'].node.setInput(
					'x',
					meshStandardBuilder1_nodes['vec3ToFloat1'].node,
					'x'
				);
				meshStandardBuilder1_nodes['floatToVec2_1'].node.setInput(
					'y',
					meshStandardBuilder1_nodes['vec3ToFloat1'].node,
					'z'
				);
				meshStandardBuilder1_nodes['checkers1'].node.setInput(
					'uv',
					meshStandardBuilder1_nodes['floatToVec2_1'].node,
					'vec2'
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'value0',
					meshStandardBuilder1_nodes['constant1'].node,
					'val'
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'value1',
					meshStandardBuilder1_nodes['constant2'].node,
					'val'
				);
				meshStandardBuilder1_nodes['mix1'].node.setInput(
					'blend',
					meshStandardBuilder1_nodes['checkers1'].node,
					'checker'
				);
				if (meshStandardBuilder1.childrenController) {
					meshStandardBuilder1.childrenController.selection.set([]);
				}
				meshStandardBuilder1.uiData.setPosition(0, 200);
				meshStandardBuilder1.params.postCreateSpareParams();
				meshStandardBuilder1.params.runOnSceneLoadHooks();
				return {node: meshStandardBuilder1, children: meshStandardBuilder1_nodes};
			}
			MAT_nodes['meshStandardBuilder1'] = create_meshStandardBuilder1(MAT);
			if (MAT.childrenController) {
				MAT.childrenController.selection.set([]);
			}
			MAT.uiData.setPosition(-200, 300);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return {node: MAT, children: MAT_nodes};
		}
		function create_box1(ground: GeoObjNode) {
			var box1 = ground.createNode('box');
			box1.setName('box1');
			const box1_nodes: ReturnedNodeDataDict = {};
			box1.uiData.setPosition(0, 100);
			box1.p.sizes.set([9.98, 1, 9.98]);
			box1.p.center.set([0, -1.5, 0]);
			box1.params.postCreateSpareParams();
			box1.params.runOnSceneLoadHooks();
			return {node: box1, children: box1_nodes};
		}
		ground_nodes['material1'] = create_material1(ground);
		ground_nodes['MAT'] = create_MAT(ground);
		ground_nodes['box1'] = create_box1(ground);
		ground_nodes['material1'].node.setInput(0, ground_nodes['box1'].node);
		if (ground.childrenController) {
			ground.childrenController.selection.set([]);
		}
		ground.uiData.setPosition(-50, -450);
		ground.flags.display.set(true);
		ground.params.postCreateSpareParams();
		ground.params.runOnSceneLoadHooks();
		return {node: ground, children: ground_nodes};
	}
	function create_lights(parentNode: RootManagerNode) {
		var lights = parentNode.createNode('geo');
		lights.setName('lights');
		const lights_nodes: ReturnedNodeDataDict = {};
		function create_hemisphereLight1(lights: GeoObjNode) {
			var hemisphereLight1 = lights.createNode('hemisphereLight');
			hemisphereLight1.setName('hemisphereLight1');
			const hemisphereLight1_nodes: ReturnedNodeDataDict = {};
			hemisphereLight1.uiData.setPosition(50, -50);
			hemisphereLight1.p.intensity.set(0.52);
			hemisphereLight1.params.postCreateSpareParams();
			hemisphereLight1.params.runOnSceneLoadHooks();
			return {node: hemisphereLight1, children: hemisphereLight1_nodes};
		}
		function create_spotLight1(lights: GeoObjNode) {
			var spotLight1 = lights.createNode('spotLight');
			spotLight1.setName('spotLight1');
			const spotLight1_nodes: ReturnedNodeDataDict = {};
			spotLight1.uiData.setPosition(300, -50);
			spotLight1.p.decay.set(0.1);
			spotLight1.p.distance.set(10);
			spotLight1.p.castShadow.set(true);
			spotLight1.params.postCreateSpareParams();
			spotLight1.params.runOnSceneLoadHooks();
			return {node: spotLight1, children: spotLight1_nodes};
		}
		function create_polarTransform1(lights: GeoObjNode) {
			var polarTransform1 = lights.createNode('polarTransform');
			polarTransform1.setName('polarTransform1');
			const polarTransform1_nodes: ReturnedNodeDataDict = {};
			polarTransform1.uiData.setPosition(300, 150);
			polarTransform1.p.center.set([0, 0.7, 0]);
			polarTransform1.p.latitude.set(25.2);
			polarTransform1.p.depth.set(3);
			polarTransform1.params.postCreateSpareParams();
			polarTransform1.params.runOnSceneLoadHooks();
			return {node: polarTransform1, children: polarTransform1_nodes};
		}
		function create_merge1(lights: GeoObjNode) {
			var merge1 = lights.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(100, 300);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		lights_nodes['hemisphereLight1'] = create_hemisphereLight1(lights);
		lights_nodes['spotLight1'] = create_spotLight1(lights);
		lights_nodes['polarTransform1'] = create_polarTransform1(lights);
		lights_nodes['merge1'] = create_merge1(lights);
		lights_nodes['polarTransform1'].node.setInput(0, lights_nodes['spotLight1'].node);
		lights_nodes['merge1'].node.setInput(0, lights_nodes['hemisphereLight1'].node);
		lights_nodes['merge1'].node.setInput(1, lights_nodes['polarTransform1'].node);
		if (lights.childrenController) {
			lights.childrenController.selection.set([lights_nodes['hemisphereLight1'].node]);
		}
		lights.uiData.setPosition(-50, -250);
		lights.flags.display.set(true);
		lights.params.postCreateSpareParams();
		lights.params.runOnSceneLoadHooks();
		return {node: lights, children: lights_nodes};
	}
	function create_cameras(parentNode: RootManagerNode) {
		var cameras = parentNode.createNode('geo');
		cameras.setName('cameras');
		const cameras_nodes: ReturnedNodeDataDict = {};
		function create_cameraControls1(cameras: GeoObjNode) {
			var cameraControls1 = cameras.createNode('cameraControls');
			cameraControls1.setName('cameraControls1');
			const cameraControls1_nodes: ReturnedNodeDataDict = {};
			function create_cameraOrbitControls1(cameraControls1: CameraControlsSopNode) {
				var cameraOrbitControls1 = cameraControls1.createNode('cameraOrbitControls');
				cameraOrbitControls1.setName('cameraOrbitControls1');
				const cameraOrbitControls1_nodes: ReturnedNodeDataDict = {};
				cameraOrbitControls1.uiData.setPosition(0, 0);
				cameraOrbitControls1.p.target.set([-0.48148989880391097, -0.29222906804685, 0.3218829791777303]);
				cameraOrbitControls1.params.postCreateSpareParams();
				cameraOrbitControls1.params.runOnSceneLoadHooks();
				return {node: cameraOrbitControls1, children: cameraOrbitControls1_nodes};
			}
			cameraControls1_nodes['cameraOrbitControls1'] = create_cameraOrbitControls1(cameraControls1);
			if (cameraControls1.childrenController) {
				cameraControls1.childrenController.selection.set([]);
			}
			cameraControls1.uiData.setPosition(0, 150);
			cameraControls1.p.node.set('cameraOrbitControls1');
			cameraControls1.params.postCreateSpareParams();
			cameraControls1.params.runOnSceneLoadHooks();
			return {node: cameraControls1, children: cameraControls1_nodes};
		}
		function create_perspectiveCamera_DEBUG(cameras: GeoObjNode) {
			var perspectiveCamera_DEBUG = cameras.createNode('perspectiveCamera');
			perspectiveCamera_DEBUG.setName('perspectiveCamera_DEBUG');
			const perspectiveCamera_DEBUG_nodes: ReturnedNodeDataDict = {};
			perspectiveCamera_DEBUG.uiData.setPosition(0, -50);
			perspectiveCamera_DEBUG.p.position.set([1.608706467168788, 1.554179582392217, 5.848803064387538]);
			perspectiveCamera_DEBUG.p.rotation.set([-18.473177623517042, 19.732503241606356, 6.435389783988498]);
			perspectiveCamera_DEBUG.params.postCreateSpareParams();
			perspectiveCamera_DEBUG.params.runOnSceneLoadHooks();
			return {node: perspectiveCamera_DEBUG, children: perspectiveCamera_DEBUG_nodes};
		}
		function create_perspectiveCamera_MAIN(cameras: GeoObjNode) {
			var perspectiveCamera_MAIN = cameras.createNode('perspectiveCamera');
			perspectiveCamera_MAIN.setName('perspectiveCamera_MAIN');
			const perspectiveCamera_MAIN_nodes: ReturnedNodeDataDict = {};
			perspectiveCamera_MAIN.uiData.setPosition(350, -50);
			perspectiveCamera_MAIN.p.position.set([3.3, 3.3, 3.3]);
			perspectiveCamera_MAIN.params.postCreateSpareParams();
			perspectiveCamera_MAIN.params.runOnSceneLoadHooks();
			return {node: perspectiveCamera_MAIN, children: perspectiveCamera_MAIN_nodes};
		}
		function create_polarTransform1(cameras: GeoObjNode) {
			var polarTransform1 = cameras.createNode('polarTransform');
			polarTransform1.setName('polarTransform1');
			const polarTransform1_nodes: ReturnedNodeDataDict = {};
			polarTransform1.uiData.setPosition(350, 150);
			polarTransform1.p.latitude.set(7.2);
			polarTransform1.p.depth.set(5.7);
			polarTransform1.params.postCreateSpareParams();
			polarTransform1.params.runOnSceneLoadHooks();
			return {node: polarTransform1, children: polarTransform1_nodes};
		}
		function create_merge1(cameras: GeoObjNode) {
			var merge1 = cameras.createNode('merge');
			merge1.setName('merge1');
			const merge1_nodes: ReturnedNodeDataDict = {};
			merge1.uiData.setPosition(150, 350);
			merge1.flags.display.set(true);
			merge1.params.postCreateSpareParams();
			merge1.params.runOnSceneLoadHooks();
			return {node: merge1, children: merge1_nodes};
		}
		cameras_nodes['cameraControls1'] = create_cameraControls1(cameras);
		cameras_nodes['perspectiveCamera_DEBUG'] = create_perspectiveCamera_DEBUG(cameras);
		cameras_nodes['perspectiveCamera_MAIN'] = create_perspectiveCamera_MAIN(cameras);
		cameras_nodes['polarTransform1'] = create_polarTransform1(cameras);
		cameras_nodes['merge1'] = create_merge1(cameras);
		cameras_nodes['cameraControls1'].node.setInput(0, cameras_nodes['perspectiveCamera_DEBUG'].node);
		cameras_nodes['polarTransform1'].node.setInput(0, cameras_nodes['perspectiveCamera_MAIN'].node);
		cameras_nodes['merge1'].node.setInput(0, cameras_nodes['cameraControls1'].node);
		cameras_nodes['merge1'].node.setInput(1, cameras_nodes['polarTransform1'].node);
		if (cameras.childrenController) {
			cameras.childrenController.selection.set([cameras_nodes['polarTransform1'].node]);
		}
		cameras.uiData.setPosition(-50, -150);
		cameras.flags.display.set(true);
		cameras.params.postCreateSpareParams();
		cameras.params.runOnSceneLoadHooks();
		return {node: cameras, children: cameras_nodes};
	}
	function create_text(parentNode: RootManagerNode) {
		var text = parentNode.createNode('geo');
		text.setName('text');
		const text_nodes: ReturnedNodeDataDict = {};
		function create_text1(text: GeoObjNode) {
			var text1 = text.createNode('text');
			text1.setName('text1');
			const text1_nodes: ReturnedNodeDataDict = {};
			text1.uiData.setPosition(-200, -100);
			text1.p.text.set('PULL & STRETCH');
			text1.p.type.set(1);
			text1.p.size.set(0.42);
			text1.p.justifyMode.set(2);
			text1.params.postCreateSpareParams();
			text1.params.runOnSceneLoadHooks();
			return {node: text1, children: text1_nodes};
		}
		function create_transform1(text: GeoObjNode) {
			var transform1 = text.createNode('transform');
			transform1.setName('transform1');
			const transform1_nodes: ReturnedNodeDataDict = {};
			transform1.uiData.setPosition(-200, 50);
			transform1.p.t.set([0, 2.1263014458517997, -3]);
			transform1.params.postCreateSpareParams();
			transform1.params.runOnSceneLoadHooks();
			return {node: transform1, children: transform1_nodes};
		}
		function create_material1(text: GeoObjNode) {
			var material1 = text.createNode('material');
			material1.setName('material1');
			const material1_nodes: ReturnedNodeDataDict = {};
			material1.uiData.setPosition(-200, 200);
			material1.flags.display.set(true);
			material1.p.material.set('../MAT/meshBasic1');
			material1.params.postCreateSpareParams();
			material1.params.runOnSceneLoadHooks();
			return {node: material1, children: material1_nodes};
		}
		function create_MAT(text: GeoObjNode) {
			var MAT = text.createNode('materialsNetwork');
			MAT.setName('MAT');
			const MAT_nodes: ReturnedNodeDataDict = {};
			function create_meshBasic1(MAT: MaterialsNetworkSopNode) {
				var meshBasic1 = MAT.createNode('meshBasic');
				meshBasic1.setName('meshBasic1');
				const meshBasic1_nodes: ReturnedNodeDataDict = {};
				meshBasic1.uiData.setPosition(-150, 0);
				meshBasic1.params.postCreateSpareParams();
				meshBasic1.params.runOnSceneLoadHooks();
				return {node: meshBasic1, children: meshBasic1_nodes};
			}
			MAT_nodes['meshBasic1'] = create_meshBasic1(MAT);
			if (MAT.childrenController) {
				MAT.childrenController.selection.set([]);
			}
			MAT.uiData.setPosition(-400, 200);
			MAT.params.postCreateSpareParams();
			MAT.params.runOnSceneLoadHooks();
			return {node: MAT, children: MAT_nodes};
		}
		text_nodes['text1'] = create_text1(text);
		text_nodes['transform1'] = create_transform1(text);
		text_nodes['material1'] = create_material1(text);
		text_nodes['MAT'] = create_MAT(text);
		text_nodes['transform1'].node.setInput(0, text_nodes['text1'].node);
		text_nodes['material1'].node.setInput(0, text_nodes['transform1'].node);
		if (text.childrenController) {
			text.childrenController.selection.set([text_nodes['text1'].node]);
		}
		text.uiData.setPosition(100, -350);
		text.flags.display.set(true);
		text.params.postCreateSpareParams();
		text.params.runOnSceneLoadHooks();
		return {node: text, children: text_nodes};
	}
	scene_root_nodes['geo1'] = create_geo1(root);
	scene_root_nodes['ground'] = create_ground(root);
	scene_root_nodes['lights'] = create_lights(root);
	scene_root_nodes['cameras'] = create_cameras(root);
	scene_root_nodes['text'] = create_text(root);
	scene.setFrame(0);
	return {
		scene,
		cameraPath: '/cameras/cameras:sopGroup/perspectiveCamera_MAIN',
		focusedNodes: scene_root_nodes['geo1'].node.children(),
		autoLayout: false,
		playWhenLoaded: true,
	};
}
